import { SECTION_IDS } from './links';
import executiveSummaryImg from '../assets/v2-report-executive-summary.jpg';
import riskComplianceImg from '../assets/v2-report-risk-compliance.jpg';
import costSavingsImg from '../assets/v2-report-cost-savings.jpg';
import legacyDecommissionImg from '../assets/v2-report-legacy-decommission.jpg';
import vulnerabilityImg from '../assets/v2-report-vulnerability.jpg';
import threatIntelImg from '../assets/v2-report-threat-intel.jpg';
import aiSocImg from '../assets/v2-report-ai-soc.jpg';
import techPortfolioImg from '../assets/v2-report-tech-portfolio.jpg';
import governanceImg from '../assets/v2-report-governance.jpg';

/**
 * The nine report sections — Figma `3jmrgfK0vysbMIQ8JgzKAV`, node `2:90`.
 *
 * The 2026-07 refresh took this from six sections to nine: Executive Summary,
 * Risk & Compliance and Cost Savings survive; System Inventory, Usage &
 * Utilization and Dependency & Architecture were replaced by six new ones.
 *
 * Layout is unchanged from the previous design (verified against node `2:211`):
 * a 592×486 gradient panel with a 430×427 masked screenshot flush to its
 * bottom-right, beside a text column. Two differences:
 *  · the `div.tagline` pill is `hidden` on all nine, so no pill is rendered
 *  · the bullet column gap is 60px (was 40px)
 *
 * INVARIANT: `imageSide` must strictly alternate right / left down the list.
 * `imageSide: 'left'` is also what draws the grey band, so two adjacent 'left'
 * entries would render two bands in a row. Matches Figma: R L R L R L R L R.
 *
 * Gradients are `linear-gradient(to top, <solid> → <transparent>)` with a 2px
 * border on top/left/bottom and an 8px radius. Only six distinct pairs cover the
 * nine sections — indigo repeats on 5/7/9 and slate on 6/8, which is consistent
 * with the new sections having been duplicated from the old ones.
 */
export interface ReportSection {
  id: string;
  /** Figma node the copy, gradient and screenshot came from. */
  node: string;
  heading: string;
  copy: string;
  /** Bullet columns, matching the design's split. */
  bulletColumns: readonly (readonly string[])[];
  image: string;
  imageAlt: string;
  /** 'right' = text left / image right (no band). 'left' = image left (grey band). */
  imageSide: 'left' | 'right';
  /** Gradient + border classes lifted verbatim from the Figma rectangle. */
  frameClass: string;
}

export const REPORT_SECTIONS: readonly ReportSection[] = [
  {
    id: SECTION_IDS.executiveSummary,
    node: '2:91',
    heading: 'Executive Summary Report',
    copy: "Get an immediate, high-level overview of your network's legacy systems. This dashboard provides the critical metrics needed to align stakeholders and drive your System Transitions Governance strategy forward.",
    bulletColumns: [
      [
        'Total legacy systems identified',
        'Total potential annual savings (£)',
        'Number of high-risk systems',
      ],
      [
        'Estimated System Transition Governance timeline (months to completion)',
        'Net savings (including energy and carbon reduction)',
      ],
    ],
    image: executiveSummaryImg,
    imageAlt:
      'Executive Summary Report dashboard showing total legacy systems, annual savings and high-risk system counts',
    imageSide: 'right',
    frameClass: 'from-[#e87722] to-[rgba(232,119,34,0)] border-[#e87722]', // 2:130
  },
  {
    id: SECTION_IDS.risk,
    node: '2:134',
    heading: 'Risk & Compliance Report',
    copy: 'Proactively identify and neutralize vulnerabilities hiding in forgotten systems. Surface compliance gaps and data security risks before they become active threats or regulatory findings.',
    bulletColumns: [
      [
        'Compliance issues (GDPR, NIS2, DORA, ISO 27001)',
        'Data security risks',
        'Operational disruption risk',
        'Regulatory exposure quantified by system',
      ],
    ],
    image: riskComplianceImg,
    imageAlt:
      'Risk & Compliance Report dashboard showing risk scores, severity counts and prioritised compliance issues',
    imageSide: 'left',
    frameClass: 'from-[#881337] to-[rgba(190,18,60,0)] border-[#881337]', // 2:136
  },
  {
    id: SECTION_IDS.costSavings,
    node: '2:172',
    heading: 'Cost Savings Breakdown Report',
    copy: 'Gain granular visibility into exactly where your IT budget is bleeding. Break down redundant costs across licensing, infrastructure, support and track realized savings month by month as systems are retired.',
    bulletColumns: [
      [
        'Licensing cost analysis by system and vendor',
        'Infrastructure costs (hosting, cloud, hardware)',
        'Support and maintenance fees',
      ],
      ['Trend over time - monthly savings realization'],
    ],
    image: costSavingsImg,
    imageAlt:
      'Cost Savings Breakdown Report dashboard showing licensing, infrastructure and support cost analysis',
    imageSide: 'right',
    frameClass: 'from-[#0f2356] to-[rgba(26,59,143,0)] border-[#0f2356]', // 2:207
  },
  {
    id: SECTION_IDS.legacyDecommission,
    node: '2:211',
    heading: 'Legacy System Decommissioning',
    copy: 'Identify legacy systems that are draining resources and prioritise decommissioning with confidence. Analyse dependencies, business impact and operational risk to build evidence-based business cases, reduce technical debt and unlock measurable cost savings across your technology estate.',
    // Figma duplicates "Identify retirement candidates" (nodes 2:229 and 2:233).
    // The repeat is dropped here on instruction — restore it only if the design
    // turns out to have meant two distinct bullets.
    bulletColumns: [
      ['Identify retirement candidates', 'Business case generation'],
      ['Cost savings analysis'],
    ],
    image: legacyDecommissionImg,
    imageAlt:
      'Legacy System Decommissioning dashboard showing retirement candidates and business case analysis',
    imageSide: 'left',
    frameClass: 'from-[#042f2e] to-[rgba(15,118,110,0)] border-[#042f2e]', // 2:213
  },
  {
    id: SECTION_IDS.vulnerability,
    node: '2:250',
    heading: 'Vulnerability Management',
    copy: 'Reduce cyber risk by focusing on the vulnerabilities that matter most. Prioritise remediation based on business impact, exploitability and operational risk to ensure security teams focus on the highest-value actions while improving your overall security posture.',
    // TODO(design): the 4th bullet is left over from the old "Usage & Utilization"
    // section this one replaced. Reproduced verbatim per Figma.
    bulletColumns: [
      ['Prioritise vulnerabilities', 'Business impact analysis', 'Risk-based remediation'],
      ['Resource underutilization alerts'],
    ],
    image: vulnerabilityImg,
    imageAlt:
      'Vulnerability Management dashboard showing prioritised vulnerabilities and business impact analysis',
    imageSide: 'right',
    frameClass: 'from-[#312e81] to-[rgba(79,70,229,0)] border-[#312e81]', // 2:285
  },
  {
    id: SECTION_IDS.threatIntel,
    node: '2:289',
    heading: 'Threat Intelligence',
    copy: 'Stay ahead of evolving cyber threats with actionable intelligence. Monitor emerging threats, threat actors and global attack trends through premium intelligence feeds, with executive reporting that turns technical data into strategic business insight.',
    // TODO(design): the 4th bullet is left over from the old "Dependency &
    // Architecture" section. Reproduced verbatim per Figma.
    bulletColumns: [
      ['External threat intelligence', 'Emerging threats & threat actors', 'Executive reporting'],
      ['Integration type tracking (API, batch, manual, real-time)'],
    ],
    image: threatIntelImg,
    imageAlt:
      'Threat Intelligence dashboard showing a global threat map, emerging threats and threat actors',
    imageSide: 'left',
    frameClass: 'from-[#0f172a] to-[rgba(71,85,105,0)] border-[#0f172a]', // 2:291
  },
  {
    id: SECTION_IDS.aiSoc,
    node: '2:327',
    heading: 'Orbis Security Operations',
    copy: 'Accelerate security operations with Orbis analysis and investigation. Empower security teams with intelligent investigations, advanced analytics and operational insights that reduce response times and improve decision-making across your security operations.',
    // TODO(design): 4th bullet left over from the old "Usage & Utilization" section.
    bulletColumns: [
      ['Orbis investigations', 'Security analytics', 'Operational insights'],
      ['Resource underutilization alerts'],
    ],
    image: aiSocImg,
    imageAlt:
      'Orbis Security Operations dashboard showing Orbis investigations and security analytics',
    imageSide: 'right',
    frameClass: 'from-[#312e81] to-[rgba(79,70,229,0)] border-[#312e81]', // 2:362
  },
  {
    id: SECTION_IDS.techPortfolio,
    node: '2:366',
    heading: 'Technology Portfolio Intelligence',
    copy: 'Gain a complete view of your technology estate to make smarter investment decisions. Consolidate insights across applications, infrastructure and business services to improve visibility, prioritise investments and optimise your technology portfolio.',
    // TODO(design): 4th bullet left over from the old "Dependency & Architecture" section.
    bulletColumns: [
      ['Technology estate visibility', 'Executive dashboards', 'Investment prioritization'],
      ['Integration type tracking (API, batch, manual, real-time)'],
    ],
    image: techPortfolioImg,
    imageAlt:
      'Technology Portfolio Intelligence dashboard showing estate visibility and investment prioritisation',
    imageSide: 'left',
    frameClass: 'from-[#0f172a] to-[rgba(71,85,105,0)] border-[#0f172a]', // 2:368
  },
  {
    id: SECTION_IDS.governance,
    node: '2:404',
    heading: 'Governance & Compliance',
    copy: 'Strengthen governance with transparent decision-making and audit-ready reporting. Standardise workflows, maintain complete audit trails and provide executive and board-level reporting to support compliance, accountability and informed technology governance.',
    // TODO(design): 4th bullet left over from the old "Usage & Utilization" section.
    bulletColumns: [
      ['Decision workflows', 'Audit trails', 'Board reporting'],
      ['Resource underutilization alerts'],
    ],
    image: governanceImg,
    imageAlt:
      'Governance & Compliance dashboard showing decision workflows, audit trails and board reporting',
    imageSide: 'right',
    frameClass: 'from-[#312e81] to-[rgba(79,70,229,0)] border-[#312e81]', // 2:439
  },
];
